import { ACHIEVEMENT_RULES, clearAchievementId } from '@/config/achievements';
import { addMedal, awardAchievement, sectorDeaths } from '@/core/meta';
import { t, type I18nKey } from '@/i18n';
import type { Player } from '@/entities/player';
import type { Hud } from '@/ui/hud';
import { sfx } from '@/audio/sfx';
import type { RunState } from './run-state';

/** Localized achievement name. */
export function achievementName(id: string): string {
  return t(`ach.${id}.name` as I18nKey);
}

/**
 * Watches a run for achievement conditions (config/achievements.ts, docs/01
 * achievements). A feat met inside a sector run is also stored as that
 * sector's medal (sector select badges), even when the achievement itself
 * was earned before.
 */
export class AchievementTracker {
  constructor(
    private readonly run: RunState,
    private readonly player: Player,
    private readonly hud: Hud,
  ) {}

  /** Build feats — checked after every boss kill and when a shop break ends. */
  checkBuild(): void {
    const R = ACHIEVEMENT_RULES;
    const weapon = this.player.weapon;
    if (weapon.equipped.length >= R.arsenalWeapons) this.feat('arsenal');
    if (weapon.gunTier >= R.topgunTier) this.feat('topgun');
    if (this.run.ownedCards.length >= R.collectorCards) this.feat('collector');
  }

  /** A boss fell: no hit since it came out = untouchable. */
  onBossKilled(): void {
    if (this.run.bossHits === 0) this.feat('untouchable');
    this.checkBuild();
  }

  /** The sector finale fell after `seconds` of fight time. */
  onSectorCleared(seconds: number): void {
    const index = this.run.sectorIndex;
    this.feat(clearAchievementId(index));
    if (seconds < ACHIEVEMENT_RULES.blitzSeconds) this.feat('blitz');
    if (sectorDeaths(index) === 0) this.feat('deathless');
    this.checkBuild();
  }

  private feat(id: string): void {
    if (this.run.mode === 'sector') addMedal(this.run.sectorIndex, id);
    if (!awardAchievement(id)) return;
    this.run.earned.push(id);
    sfx.recordToast();
    this.hud.notify(t('hud.achievement', { name: achievementName(id) }), 'record');
  }
}
